import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { useNavigate } from 'react-router-dom';
import { useSEO, SITE_URL } from '../../hooks/useSEO';

const PhishingSimulationPage = () => {
  const navigate = useNavigate();

  useSEO({
    title: 'Phishing Simulation Testing for Mortgage Teams | Mortgage Moat',
    description: 'Test your mortgage team\'s ability to spot phishing attacks. Company-wide phishing simulation + 2-week managed security trial for just $100. Identify vulnerabilities before hackers do.',
    keywords: 'phishing simulation, phishing test, mortgage security training, email security, phishing awareness',
    canonical: '/phishing-simulation',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'Phishing Simulation Testing',
        provider: { '@type': 'Organization', name: 'Mortgage Moat', url: SITE_URL },
        url: `${SITE_URL}/phishing-simulation`,
        description: 'Company-wide phishing simulation that identifies vulnerable employees and includes a 2-week trial of 24/7 managed cyber protection.',
        offers: {
          '@type': 'Offer',
          price: '100',
          priceCurrency: 'USD',
        },
        areaServed: { '@type': 'Country', name: 'United States' },
        serviceType: 'Phishing Simulation',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
          { '@type': 'ListItem', position: 2, name: 'Phishing Simulation', item: `${SITE_URL}/phishing-simulation` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Is the $100 phishing simulation refundable?',
            acceptedAnswer: { '@type': 'Answer', text: 'Yes. If you\'re not satisfied with the phishing simulation or the quality of the service, we offer a full refund within 7 days of purchase.' },
          },
          {
            '@type': 'Question',
            name: 'What happens after the 2-week managed services trial ends?',
            acceptedAnswer: { '@type': 'Answer', text: 'After 2 weeks, your managed services trial ends automatically. There\'s no automatic billing. If you want to continue with 24/7 protection, you can subscribe to our managed services at $20/user/month.' },
          },
          {
            '@type': 'Question',
            name: 'How many employees can participate in the phishing simulation?',
            acceptedAnswer: { '@type': 'Answer', text: 'The $100 covers your entire organization, regardless of size. Whether you have 10 employees or 100, everyone gets tested.' },
          },
          {
            '@type': 'Question',
            name: 'Will employees know they are being tested for phishing?',
            acceptedAnswer: { '@type': 'Answer', text: 'That\'s your choice. Some companies prefer surprise testing for accurate results, while others inform employees that security testing will occur. We can accommodate either approach.' },
          },
        ],
      },
    ],
  });

  const includes = [
    {
      icon: 'ri-mail-check-line',
      title: 'Company-Wide Phishing Test',
      description: 'Realistic phishing simulations sent to your entire team to identify vulnerabilities and security awareness gaps'
    },
    {
      icon: 'ri-shield-check-line',
      title: '2-Week Trial of 24/7 Protection',
      description: 'Experience our complete managed security services with round-the-clock monitoring and threat detection'
    },
    {
      icon: 'ri-dashboard-3-line',
      title: 'Detailed Vulnerability Report',
      description: 'Comprehensive analysis showing which employees clicked, who reported, and your overall security posture'
    },
    {
      icon: 'ri-alert-line',
      title: 'Real-Time Threat Detection',
      description: 'Active monitoring during your trial period with immediate alerts for any detected security threats'
    },
    {
      icon: 'ri-graduation-cap-line',
      title: 'Security Awareness Training',
      description: 'Access to training modules for employees who fail the phishing test to improve their awareness'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Schedule a Review',
      description: 'Direct access to cybersecurity professionals to review results and discuss next steps'
    }
  ];

  const benefits = [
    {
      stat: '$100',
      label: 'One-time investment'
    },
    {
      stat: '2 Weeks',
      label: 'Free managed services trial'
    },
    {
      stat: '100%',
      label: 'Company-wide testing'
    },
    {
      stat: '24/7',
      label: 'Expert support included'
    }
  ];

  const whatToExpect = [
    {
      day: 'Day 1',
      title: 'Quick Setup & Launch',
      description: 'We configure your phishing simulation and prepare realistic test emails tailored to your industry.',
      icon: 'ri-settings-3-line'
    },
    {
      day: 'Day 2-3',
      title: 'Phishing Test Deployed',
      description: 'Your employees receive realistic phishing emails. We track who clicks, who reports, and who ignores them.',
      icon: 'ri-mail-send-line'
    },
    {
      day: 'Day 4-7',
      title: 'Results Analysis',
      description: 'Detailed reports show your vulnerability level, employee performance, and specific security gaps.',
      icon: 'ri-bar-chart-box-line'
    },
    {
      day: 'Day 8-10',
      title: '24/7 Monitoring Active',
      description: 'Your 2-week trial of managed services begins with full security monitoring and threat detection.',
      icon: 'ri-eye-line'
    },
    {
      day: 'Day 11-13',
      title: 'Training & Remediation',
      description: 'Employees who failed receive targeted training. You get expert recommendations for improvement.',
      icon: 'ri-tools-line'
    },
    {
      day: 'Day 14',
      title: 'Final Review & Decision',
      description: 'Review complete results and decide if you want to continue with our managed security services.',
      icon: 'ri-checkbox-circle-line'
    }
  ];

  const testimonials = [
    {
      quote: "The phishing simulation was eye-opening. 40% of our staff clicked on the test email. We immediately signed up for the full managed services.",
      author: "Sarah Mitchell",
      role: "Operations Manager",
      company: "Midwest Mortgage Solutions"
    },
    {
      quote: "Best $100 we ever spent. The simulation revealed vulnerabilities we didn't know existed, and the 2-week trial proved the value of 24/7 monitoring.",
      author: "David Chen",
      role: "IT Director",
      company: "Pacific Coast Lending"
    },
    {
      quote: "The detailed report was incredibly valuable. Even if we hadn't continued with the service, knowing our security gaps was worth the investment.",
      author: "Jennifer Rodriguez",
      role: "Compliance Officer",
      company: "Summit Financial Group"
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-red-600 to-red-800 text-white py-24">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="inline-block bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-bold mb-6">
              PHISHING SIMULATION TESTING
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Find Out Who's Clicking Before Hackers Do
            </h1>
            <p className="text-xl mb-8 text-white/90">
              Test your team's ability to spot phishing attacks with realistic simulations that identify vulnerabilities and provide targeted training.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="/purchase/phishing-simulation"
                className="inline-flex items-center justify-center gap-2 bg-cyan-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-cyan-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Get Started Now
                <i className="ri-arrow-right-line"></i>
              </a>
              <a 
                href="/schedule-call"
                className="inline-flex items-center justify-center gap-2 bg-white text-cyan-600 px-8 py-4 rounded-lg font-semibold hover:bg-cyan-50 border-2 border-cyan-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Learn More
                <i className="ri-information-line"></i>
              </a>
            </div>
            <p className="text-sm text-white/75 mt-6">
              One-time $100 payment • Includes 2-week managed services trial • No long-term commitment
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="text-5xl font-bold text-red-600 mb-2">{benefit.stat}</div>
                <p className="text-gray-600 font-semibold">{benefit.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              What's Included for $100
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A complete phishing simulation for your entire company plus 2 weeks of full managed security services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {includes.map((item, index) => (
              <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center bg-red-100 rounded-xl mb-6">
                  <i className={`${item.icon} text-3xl text-red-600`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What to Expect */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Your 2-Week Experience
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Here's exactly what happens from phishing test to final results.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whatToExpect.map((phase, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-8">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 flex items-center justify-center bg-red-600 text-white rounded-lg flex-shrink-0">
                    <i className={`${phase.icon} text-2xl`}></i>
                  </div>
                  <div>
                    <div className="text-sm font-bold text-red-600 uppercase">{phase.day}</div>
                    <h3 className="text-lg font-bold text-gray-900">{phase.title}</h3>
                  </div>
                </div>
                <p className="text-gray-600">{phase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Managed Services Highlight - NEW SECTION */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-gradient-to-br from-red-600 to-red-800 rounded-2xl shadow-2xl overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              <div className="p-12 flex flex-col justify-center text-white">
                <div className="inline-block bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-bold mb-6 self-start">
                  INCLUDED IN YOUR TRIAL
                </div>
                <h3 className="text-3xl md:text-4xl font-bold mb-6">
                  Experience 24/7 Managed Cyber Protection
                </h3>
                <p className="text-lg text-white/90 mb-6">
                  Your $100 phishing simulation includes a 2-week trial of our complete managed cyber protection service – giving you a taste of enterprise-level security at just $20/user/month.
                </p>
                <ul className="space-y-3 mb-8">
                  {[
                    '24/7 security monitoring & threat detection',
                    'Endpoint protection for all devices',
                    'Email security gateway',
                    'Real-time threat alerts',
                    'Incident response team',
                    'Security operations center (SOC)',
                    'Vulnerability scanning & patching',
                    'Monthly executive reports'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-white text-xl"></i>
                      </div>
                      <span className="text-white/90">{item}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-4">
                  <Button 
                    variant="secondary" 
                    size="lg" 
                    className="px-8 py-4 font-bold whitespace-nowrap"
                    onClick={() => navigate('/managed-services')}
                  >
                    Learn More About 24/7 Protection
                  </Button>
                </div>
              </div>
              <div className="relative h-96 lg:h-auto">
                <img 
                  src="https://static.readdy.ai/image/807f7a13fa3a4707437af02957aa93c9/94d0f71b6ceceeac4b6e7dd2fdc9f1fe.png"
                  alt="24/7 Managed Cyber Protection"
                  className="w-full h-full object-cover object-left"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why This Matters */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Why Every Mortgage Company Needs This
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Phishing is the #1 cause of data breaches in financial services. For just $100, you'll know exactly where your vulnerabilities are.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-lg flex-shrink-0">
                    <i className="ri-eye-line text-2xl text-red-600"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">See Your Real Vulnerabilities</h3>
                    <p className="text-gray-600">Discover exactly which employees would fall for a phishing attack and what threats exist in your systems.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-lg flex-shrink-0">
                    <i className="ri-shield-check-line text-2xl text-red-600"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Experience Real Protection</h3>
                    <p className="text-gray-600">See how 24/7 monitoring and expert support work to protect your business during the 2-week trial.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-lg flex-shrink-0">
                    <i className="ri-bar-chart-box-line text-2xl text-red-600"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Get Actionable Insights</h3>
                    <p className="text-gray-600">Receive detailed reports and expert recommendations you can use whether you continue or not.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-lg flex-shrink-0">
                    <i className="ri-money-dollar-circle-line text-2xl text-red-600"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Minimal Investment, Maximum Value</h3>
                    <p className="text-gray-600">For just $100, get insights that could prevent a breach costing hundreds of thousands of dollars.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative h-96 lg:h-full min-h-[600px]">
              <img 
                src="https://public.readdy.ai/ai/img_res/1dfbd0eaace89f7ca6c49d3f1758be45.jpg"
                alt="Security Success"
                className="w-full h-full object-cover object-top rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              What Customers Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Real feedback from mortgage professionals who invested in the phishing simulation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-8">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-xl"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.quote}"</p>
                <div className="border-t border-gray-200 pt-4">
                  <div className="font-bold text-gray-900">{testimonial.author}</div>
                  <div className="text-sm text-gray-600">{testimonial.role}</div>
                  <div className="text-sm text-gray-500">{testimonial.company}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Get Started */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Getting Started is Simple
            </h2>
            <p className="text-xl text-gray-600">
              Three easy steps to launch your phishing simulation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-red-600 text-white text-3xl font-bold rounded-full mx-auto mb-6">
                1
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Purchase & Schedule</h3>
              <p className="text-gray-600">Pay $100 and schedule your simulation. We'll coordinate the best timing for your team.</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-red-600 text-white text-3xl font-bold rounded-full mx-auto mb-6">
                2
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Quick Setup Call</h3>
              <p className="text-gray-600">We'll have a brief call to configure your simulation and answer any questions.</p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 flex items-center justify-center bg-red-600 text-white text-3xl font-bold rounded-full mx-auto mb-6">
                3
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Launch & Monitor</h3>
              <p className="text-gray-600">Your phishing test launches and your 2-week managed services trial begins immediately.</p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-cyan-50 to-teal-50 p-8 rounded-xl border-2 border-cyan-200">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-cyan-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-shield-check-line text-white text-2xl"></i>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Ready to Test Your Team?</h3>
                <p className="text-gray-700">
                  Start protecting your organization from phishing attacks today with our comprehensive simulation platform.
                </p>
              </div>
            </div>
            <a 
              href="/purchase/phishing-simulation"
              className="inline-flex items-center justify-center gap-2 bg-cyan-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-cyan-700 transition-colors w-full whitespace-nowrap cursor-pointer"
            >
              Get Phishing Simulation Now
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
            Common Questions
          </h2>
          
          <div className="space-y-6">
            {[
              {
                question: 'Is the $100 refundable?',
                answer: 'Yes. If you\'re not satisfied with the phishing simulation or the quality of the service, we offer a full refund within 7 days of purchase.'
              },
              {
                question: 'What happens after the 2-week trial ends?',
                answer: 'After 2 weeks, your managed services trial ends automatically. There\'s no automatic billing. If you want to continue with 24/7 protection, you can subscribe to our managed services at $20/user/month.'
              },
              {
                question: 'Will my employees know they\'re being tested?',
                answer: 'That\'s your choice. Some companies prefer surprise testing for accurate results, while others inform employees that security testing will occur. We can accommodate either approach.'
              },
              {
                question: 'How many employees can participate?',
                answer: 'The $100 covers your entire organization, regardless of size. Whether you have 10 employees or 100, everyone gets tested.'
              },
              {
                question: 'What if we discover serious vulnerabilities?',
                answer: 'We\'ll alert you immediately to any critical security issues discovered during testing or monitoring, and provide guidance on addressing them – whether you continue with our services or not.'
              },
              {
                question: 'Can we do the simulation more than once?',
                answer: 'This package includes one company-wide simulation. If you want ongoing phishing tests, our managed services include monthly simulations as part of the subscription.'
              },
              {
                question: 'How realistic are the phishing emails?',
                answer: 'Very realistic. We use the same tactics real hackers use, customized for the mortgage industry. Many employees can\'t tell the difference between our tests and real phishing attempts.'
              },
              {
                question: 'Do we get to keep the reports?',
                answer: 'Absolutely. All reports, analysis, and recommendations are yours to keep, even if you don\'t continue with managed services.'
              }
            ].map((faq, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                <p className="text-gray-600">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-red-600 to-red-800 text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Don't Wait for a Breach to Take Security Seriously
          </h2>
          <p className="text-xl mb-8 text-white/90">
            For just $100, discover exactly how vulnerable your mortgage business is to phishing attacks.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/purchase/phishing-simulation"
              className="inline-flex items-center justify-center gap-2 bg-cyan-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-cyan-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Phishing Simulation
              <i className="ri-arrow-right-line"></i>
            </a>
            <a 
              href="/schedule-call"
              className="inline-flex items-center justify-center gap-2 bg-white text-cyan-600 px-8 py-4 rounded-lg font-semibold hover:bg-cyan-50 border-2 border-cyan-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Schedule a Call
              <i className="ri-calendar-line"></i>
            </a>
          </div>
          <p className="text-sm text-white/75 mt-6">
            One-time $100 payment • 2-week managed services trial included • Money-back guarantee
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PhishingSimulationPage;
